import React, { useState, useEffect } from 'react';
import Styles from './OrderSummary.module.scss';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import { CiLock } from 'react-icons/ci';
import { CouponElement } from 'Containers/Subscription/CouponElement';
import { removeRedundantDecimalPlaces } from 'helpers';
import { useDispatch, useSelector } from 'react-redux';
import { setLoader } from 'redux/slices/loader';
import { actions } from 'redux/slices/auth';
import { toast } from 'react-toastify';
import { updateSubscription, startTrial, refreshSubscription, applyCouponDiscount, confirmCardPayment, getCurrentSubscription } from 'services/subscriptionService';
import { createLifetimePaymentIntent, confirmLifetimePayment } from 'services/lifetimeSubscriptionService';
import { error as renderError } from 'helpers/toaster.js';
import { useNavigate } from 'react-router-dom';
import { CONFIRM_SUBSCRIPTION } from 'constants';
import ConfirmationModal from 'Containers/Modal/ConfirmationModal/ConfirmationModal';
import moment from 'moment';

const OrderSummary = ({
  newSubscription,
  setIsDisabledSwitch,
  refetch,
  isTrial = false,
  product = {},
  setOrderSummary,
  paymentMethodData,
  planId,
  selectedPlanType,
  isLifetime = false,
}) => {
  const [couponName, setCouponName] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const { subscriptions } = user ?? {};
  const {
    current_subscription: currentSubscription,
    last_subscription: lastSubscription,
  } = subscriptions ?? {};
  const { end_date: endDate } = currentSubscription ?? {};

  const finalPrice =
    discountPercentage > 0 ? discountedPrice : newSubscription?.amount || 0;

  const isMonthly = newSubscription?.interval === 'month';
  const isYearly = !isMonthly && !isLifetime;

  function getCookie(name) {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }
  
  const applyCoupon = async (couponName, priceId) => {
    dispatch(
      setLoader({
        isLoading: true,
        heading: 'Applying coupon',
        description: 'Please wait...',
      }),
    );
    try {
      const response = await applyCouponDiscount(couponName, priceId);
      const responseData = response.data;
      if (responseData.valid) {
        setDiscountedPrice(responseData.discounted_price);
        setDiscountPercentage(responseData.discount_percentage);
        toast.success('Coupon applied successfully');
      } else {
        toast.error(responseData.message);
      }
      setShowCouponForm(true);
      setIsDisabledSwitch(false);
    } catch (error) {
      toast.error('Oops! An error occurred while applying the coupon', error);
    }
    dispatch(setLoader({ isLoading: false }));
  };

  useEffect(() => {
    const rewardfulCookie = getCookie('rewardful.referral');
    if (rewardfulCookie) {
      try {
        // Parse JSON string
        const parsedData = JSON.parse(rewardfulCookie);
        setCouponName(parsedData?.coupon?.name);
        console.log(parsedData);
        toggleCouponForm();
        parsedData?.coupon?.name &&
          applyCoupon(parsedData?.coupon?.name, newSubscription?.id);
      } catch (error) {
        console.error('Error parsing cookie:', error);
      }
    }
  }, []);

  const handleLifetimeSubscribe = async () => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Processing Lifetime Payment!',
          description: 'Please wait...',
        }),
      );

      // Check if user has payment methods first
      if (!paymentMethodData || paymentMethodData.length === 0) {
        dispatch(setLoader({ isLoading: false }));
        toast.error(
          'Please add a payment method first before purchasing lifetime subscription.',
        );
        return;
      }

      // Create and confirm payment intent immediately (uses default payment method)
      // Pass the selected lifetime plan ID, coupon code and discounted price
      const response = await createLifetimePaymentIntent(newSubscription?.id, couponName, discountedPrice);
      const paymentIntentId = response.data.client_secret.split('_secret_')[0];

      // Create subscription after successful payment
      await confirmLifetimePayment(paymentIntentId);

      window.rewardful('convert', { email: user.email });

      // Refresh subscription state to update frontend
      await refetch();

      // Also fetch and update user subscription state in Redux
      try {
        const currentSubscriptionRes = await getCurrentSubscription();
        const { data: subscriptionData } = currentSubscriptionRes || {};

        if (subscriptionData) {
          dispatch(
            actions.updateUser({
              user: { subscriptions: subscriptionData },
            }),
          );
        }
      } catch (error) {
        console.error('Error updating user subscription state:', error);
      }

      dispatch(setLoader({ isLoading: false }));
      toast.success(
        '🎉 Lifetime subscription activated! Welcome to lifetime access!',
      );

      // Navigate to existing subscription success page
      const { stripe_price_id } = newSubscription || {};
      const lifetimePlan = newSubscription;
      if (lifetimePlan) {
        navigate(
          `/confirm-subscription/plan/${lifetimePlan.type}/price/${lifetimePlan.amount}/type/lifetime`,
        );
      } else {
        navigate('/dashboard'); // Fallback
      }
    } catch (error) {
      dispatch(setLoader({ isLoading: false }));
      console.error('Lifetime subscription error:', error);

      // Check for specific error messages
      if (error.response?.data?.error?.includes('payment method')) {
        toast.error(
          'Please add a payment method first before purchasing lifetime subscription.',
        );
      } else {
        renderError(error);
      }
    }
  };

  const handleSubscribe = async () => {
    const { id, interval } = newSubscription || {};
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Subscribing Plan!',
          description: 'Please wait...',
        }),
      );

      const subscription = await updateSubscription(
        id,
        couponName,
        discountedPrice,
      );

      // Handle payment confirmation for returning customers
      // Check if payment confirmation is needed (response contains type and clientSecret)
      if (
        subscription?.data?.type === 'payment' &&
        subscription?.data?.clientSecret
      ) {
        dispatch(
          setLoader({
            isLoading: true,
            heading: 'Confirming Payment!',
            description: 'Please wait while we process your payment...',
          }),
        );

        const { error } = await confirmCardPayment(
          subscription.data.clientSecret,
        );

        if (error) {
          dispatch(setLoader({ isLoading: false }));
          toast.error(`Payment failed: ${error.message}`);
          return;
        }

        // Payment confirmed, now refresh the subscription
        const paymentIntent =
          subscription.data.clientSecret.split('_secret_')[0];
        await refreshSubscription(paymentIntent);
        toast.success('Payment confirmed and subscription activated!');
      } else {
        toast.success('Plan Subscribed Successfully');
      }

      window.rewardful('convert', { email: user.email });
      // dispatch(
      //   actions.updateUser({ user: { subscriptions: { currentSubscription: subscription?.data?.subscription } } }),
      // );
      // fetchPlansData();
      refetch();
      dispatch(setLoader({ isLoading: false }));
      if (
        !currentSubscription &&
        lastSubscription?.active === false &&
        lastSubscription?.status === 'canceled'
      ) {
        navigate(
          `/resubscribe-confirmation/plan/${newSubscription?.type}/price/${finalPrice}/type/${newSubscription?.interval}`,
        );
      } else {
        navigate(
          `/${CONFIRM_SUBSCRIPTION}/plan/${newSubscription?.type}/price/${finalPrice}/type/${newSubscription?.interval}`,
        );
      }
    } catch (error) {
      dispatch(setLoader({ isLoading: false }));
      renderError(error);
    }
  };

  const initiateTrial = async (product) => {
    // const { id, type, amount, interval } = product || {};
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Starting Trial!',
          description: 'Please wait...',
        }),
      );
      await startTrial(planId, couponName);
      window.rewardful('convert', { email: user.email });

      // fetchPlansData();
      refetch();
      dispatch(setLoader({ isLoading: false }));
      toast.success('Free Trial Started Successfully');
      setOrderSummary &&
        setOrderSummary({
          planType: newSubscription?.type,
          finalPrice,
          interval: newSubscription?.interval,
        });
      // dispatch(setCurrentStep(6))
      navigate(
        `/summary/plan/${newSubscription?.type}/price/${finalPrice}/type/${newSubscription?.interval}?trial=${isTrial}`,
      );
    } catch (error) {
      dispatch(setLoader({ isLoading: false }));
      renderError(error);
    }
  };

  const getModalDescription = () => {
    if (isTrial) {
      return "Your 3-days free trial will start today where you can explore all features. You won't be charged during the trial, and you can cancel anytime before it ends.";
    } else if (isLifetime) {
      // Check if user has an existing subscription
      const featureType = newSubscription?.type?.includes('Business') ? 'Business' : 'Pro';
      if (currentSubscription?.active) {
        return `You're upgrading to lifetime access! Your current subscription will be cancelled and replaced with unlimited lifetime access to all ${featureType} features. This is a one-time payment - no more recurring charges, ever.`;
      } else {
        return `Welcome to Vocable! You're about to get lifetime access to all ${featureType} features with a single payment. No monthly bills, no expiration date - just unlimited access forever.`;
      }
    } else if (
      currentSubscription?.active &&
      currentSubscription?.status === 'trial'
    ) {
      return `Your trial will continue until ${moment(endDate).format('MM/DD/YYYY')}. Your plan will be upgraded immediately, but you won't be charged until your trial ends.`;
    } else if (
      currentSubscription?.active &&
      planId !== currentSubscription?.stripe_price?.id
    ) {
      return 'Your plan will be upgraded. Your current plan ends today, and the new plan starts immediately with an adjusted billing cycle. The payment will be prorated accordingly, reflecting the cost of the new plan.';
    } else if (
      currentSubscription?.active &&
      planId === currentSubscription?.stripe_price?.id &&
      selectedPlanType === currentSubscription?.stripe_price?.interval
    ) {
      return 'You plan will be resubscribed. Your previous subscription resumes seamlessly, and billing will continue as scheduled.';
    } else if (
      currentSubscription?.active &&
      planId === currentSubscription?.stripe_price?.id &&
      selectedPlanType !== currentSubscription?.stripe_price?.interval
    ) {
      return 'Your plan will be upgraded. Your current plan ends today, and the new plan starts immediately with an adjusted billing cycle. The payment will be prorated accordingly, reflecting the cost of the new plan.';
    } else {
      return 'Are you sure you want to proceed with the payment for your subscription? Please review the details carefully, as this action will be final and cannot be undone.';
    }
  };

  const handlePayment = () => {
    if (isTrial) {
      initiateTrial(product);
    } else if (isLifetime) {
      handleLifetimeSubscribe();
    } else {
      handleSubscribe();
    }
  };

  const toggleCouponForm = () => {
    setShowCouponForm(!showCouponForm);
  };

  const handleCancel = () => {
    setShowCouponForm(false);
    setCouponName('');
    setDiscountedPrice('');
  };
  
  return (
    <div>
      <div className={Styles.card_container}>
        <div className={Styles.card_container__heading}>ORDER SUMMARY</div>
        <div className={Styles.card_container__top}>
          <div className={Styles.card_container__top__detail}>
            <span className={Styles.card_container__top__detail__heading}>
              {newSubscription?.type}
            </span>
            <span className={Styles.card_container__top__detail__amount}>
              ${removeRedundantDecimalPlaces(newSubscription?.amount)}
            </span>
          </div>
          <div className={Styles.card_container__top__detail}>
            <span className={Styles.card_container__top__detail__heading}>
              Discount
            </span>
            <span className={Styles.card_container__top__detail__amount}>
              {removeRedundantDecimalPlaces(parseFloat(discountPercentage))}%
            </span>
          </div>
        </div>
        <div className={Styles.card_container__total}>
          <span className={Styles.card_container__total__heading}>
            Total
            {isYearly && !isLifetime &&
              ` ($${removeRedundantDecimalPlaces(finalPrice / 12)} x 12mo)`}
          </span>
          <span className={Styles.card_container__total__amount}>
            ${removeRedundantDecimalPlaces(finalPrice)}
          </span>
        </div>
        <div
          className={`${Styles.card_container__coupan} flex-column align-items-start`}
        >
          {discountPercentage === 0 && (
            <div className="d-flex w-100 justify-content-between">
              <span className={Styles.card_container__coupan__heading}>
                Do you have any coupon?
              </span>
              <button
                className={Styles.card_container__coupan__btn}
                onClick={() => {
                  showCouponForm ? handleCancel() : toggleCouponForm();
                }}
              >
                {showCouponForm ? 'Cancel' : 'Add Coupon'}
              </button>
            </div>
          )}

          {showCouponForm && (
            <div className="d-flex flex-column gap-3 w-100">
              <CouponElement
                couponName={couponName}
                setCouponName={setCouponName}
                setDiscountedPrice={setDiscountedPrice}
                setShowCouponForm={setShowCouponForm}
                setDiscountPercentage={setDiscountPercentage}
                priceId={newSubscription?.id}
                setIsDisabledSwitch={setIsDisabledSwitch}
              />
            </div>
          )}
          {discountPercentage > 0 && (
            <div>
              <p className={`${Styles.textAppiedCoupan} mr-8 d-inline-block `}>
                <span className={Styles.coupanValue}>{couponName}</span> Coupon
                code applied.
              </p>
            </div>
          )}
        </div>
        <div className={Styles.card_container__pay_btn}>
          <button
            disabled={!paymentMethodData || paymentMethodData.length === 0 || !termsAccepted}
            className={`w-100 px-3 py-2 ${Styles.black_btn}`}
            onClick={() => setCancelModalOpen(true)} // uncomment this to show pop up
            // onClick={() => handlePayment()}
          >
            {isTrial ? 'Start Free Trial' : `Pay $${removeRedundantDecimalPlaces(finalPrice)}`}
            {/* {isTrial ? 'Start Free Trial' : currentSubscription?.active && currentSubscription?.status === 'trial' ? 'Change trial plan' 
            : currentSubscription?.active && planId !== currentSubscription?.stripe_price?.id ? "Upgrade" 
            : currentSubscription?.active && planId === currentSubscription?.stripe_price?.id && selectedPlanType ===  currentSubscription?.stripe_price?.interval ? "Resubscribe" 
            : currentSubscription?.active && planId === currentSubscription?.stripe_price?.id && selectedPlanType !==  currentSubscription?.stripe_price?.interval ? "Upgrade" 
            : `Pay ${finalPrice}`}  */}
          </button>
        </div>
        
        <div className={Styles.card_container__footer}>
          <div>
            <HiOutlineExclamationCircle />
          </div>
          <span>
            {isTrial
              ? `Your 3-day free trial starts now. When it ends on ${moment()
                  .add(3, 'days')
                  .format('MM/DD/YYYY')}, you'll be automatically charged and billed every ${isMonthly ? 'month' : 'year'} until canceled.`
              : isLifetime
              ? 'One-time payment will be processed immediately upon confirmation.'
              : currentSubscription?.active && currentSubscription?.status === 'trial'
              ? `You'll be charged immediately and billed every ${isMonthly ? 'month' : 'year'} thereafter until canceled.`
              : currentSubscription?.active && (
                  planId !== currentSubscription?.stripe_price?.id ||
                  selectedPlanType !== currentSubscription?.stripe_price?.interval
                )
              ? `You'll be charged immediately (prorated) and billed every ${isMonthly ? 'month' : 'year'} thereafter until canceled.`
              : `You'll be billed every ${isMonthly ? 'month' : 'year'} until canceled. Next payment: ${moment(
                  endDate ?? new Date(),
                )
                  .add(endDate ? 0 : 1, isMonthly ? 'months' : 'years')
                  .format('MM/DD/YYYY')}.`}
          </span>
        </div>
        
        {/* Terms and Conditions Checkbox */}
        <div className={Styles.terms_checkbox_container}>
          <div className={Styles.terms_checkbox_wrapper}>
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className={Styles.terms_checkbox}
            />
            <label htmlFor="terms-checkbox" className={Styles.terms_label}>
              I understand and agree to the{' '}
              <a
                href="https://vocable.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className={Styles.terms_link}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="https://vocable.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={Styles.terms_link}
              >
                Privacy Policy
              </a>
            </label>
          </div>
        </div>
      </div>
      <div className={Styles.footer}>
        <div>
          <CiLock />
        </div>
        <span className={Styles.footer__text}>
          Your payment data will be processed and{' '}
          <span className={Styles.footer__text__underline}>
            secured by Stripe
          </span>
          <span>{` `} </span>and their stringent level of certification.
        </span>
      </div>
      <ConfirmationModal
        show={cancelModalOpen}
        onHide={() => setCancelModalOpen(false)}
        onDelete={handlePayment}
        modalHeading="Confirm Subscription"
        modalDescription={getModalDescription()}
        actionText="Proceed"
        loaderHeading="Processing!"
      />
    </div>
  );
};

export default OrderSummary;